import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';

function formatUTCtoLocal24H(utcString) {
  const date = new Date(utcString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
}

export default function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({
    id: '',
    bot_name: '',
    status: '',
    message: '',
    log_time: '',
  });
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);

  const fetchLogs = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location = '/login';
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get('/api/logs', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(response.data);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        window.location = '/login';
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    const interval = setInterval(fetchLogs, 5000); // обновлять каждые 5 секунд (чтобы не слишком часто)
    return () => clearInterval(interval);
  }, []);

  // Обновляем фильтр
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Отфильтрованные логи, мемоизированы для оптимизации
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      return Object.entries(filters).every(([key, filterValue]) => {
        if (!filterValue) return true; // пустой фильтр - пропускаем
        const logValue = log[key]?.toString().toLowerCase() || '';
        return logValue.includes(filterValue.toLowerCase());
      });
    }).slice(0, pageSize);
  }, [logs, filters, pageSize]);

  // Очистка логов (кнопка)
  const clearLogs = async () => {
    if (!window.confirm('Вы уверены, что хотите очистить все логи? Это действие необратимо!')) return;

    const token = localStorage.getItem('token');
    if (!token) {
      window.location = '/login';
      return;
    }

    try {
      await axios.delete('/api/logs', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs([]); // очищаем локально
    } catch (err) {
      console.error('Ошибка при очистке логов:', err);
      alert('Не удалось очистить логи.');
    }
  };

  return (
    <div className="p-4">
      <h1 className="text-3xl font-bold mb-4 flex items-center justify-between">
        Logs Page
        <button
          onClick={clearLogs}
          className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          disabled={loading}
          title="Очистить все логи"
        >
          Очистить логи
        </button>
      </h1>

      <div className="mb-2">
        Показать строк:
        <select
          value={pageSize}
          onChange={e => setPageSize(Number(e.target.value))}
          className="ml-2 border rounded px-2 py-1"
        >
          {[10, 20, 50, 100].map(size => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
      </div>

      <table className="min-w-full border border-gray-300">
        <thead>
          <tr className="bg-gray-100">
            {['id', 'bot_name', 'status', 'message', 'log_time'].map(column => (
              <th key={column} className="border p-2">
                <div className="font-semibold capitalize">{column.replace('_', ' ')}</div>
                <input
                  type="text"
                  value={filters[column]}
                  onChange={e => handleFilterChange(column, e.target.value)}
                  className="w-full border rounded px-1 py-0.5 mt-1"
                  placeholder={`Фильтр по ${column}`}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredLogs.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center p-4 text-gray-500">
                Нет логов, соответствующих фильтрам.
              </td>
            </tr>
          ) : (
            filteredLogs.map(log => (
              <tr key={log.id}>
                <td className="border p-2">{log.id}</td>
                <td className="border p-2">{log.bot_name}</td>
                <td className="border p-2">{log.status}</td>
                <td className="border p-2">{log.message}</td>
                <td className="border p-2">{formatUTCtoLocal24H(log.log_time)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
